import { useEffect, useState } from 'react';

/**
 * Page-2 artwork collection state for the QuoteCalculator wizard.
 *
 * Page 2 of the quote wizard combines Size + Quantity + a MANDATORY
 * artwork drop-zone. The dropzone uploads to
 * `/api/files/artwork/staging-upload` the moment the user drops a
 * file, and the resulting staging artwork_id is adopted into the
 * order on Place Order via POST /artwork/staging/{id}/attach.
 *
 * This hook bundles the four pieces of state that Page 2 owns
 * (file, staging id, cutline shape, instructions) plus the derived
 * `selectedShape` mapping that pricing / submission / PDF export
 * read off:
 *   - cutlineShape === 'custom'  → selectedShape = 'die-cut'
 *   - cutlineShape ∈ {rectangle, rounded, circle} → 'standard'
 *
 * Callers wire `selectedShape` + the setter into the wizard so the
 * Page-1 die-cut/standard radio group never has to be duplicated on
 * Page 2.
 *
 * Returns an object with `{ artwork, setters... , selectedShape, setSelectedShape }`
 * shaped to plug straight into the existing QuoteCalculator render.
 */
export function usePage2ArtworkState() {
  // The actual File object (kept for retry-on-attach if the staging
  // upload returns a 5xx and the user clicks Place Order).
  const [page2ArtworkFile, setPage2ArtworkFile] = useState(null);
  // The staging artwork_id returned by /artwork/staging-upload.
  const [page2ArtworkId, setPage2ArtworkId] = useState(null);
  // 'custom' | 'rectangle' | 'rounded' | 'circle' (or '' before pick).
  const [page2CutlineShape, setPage2CutlineShape] = useState('');
  // Free-text production instructions (white ink notes, "leave bleed
  // alone", etc.). Persisted onto the order via /save-instructions.
  const [page2ArtworkInstructions, setPage2ArtworkInstructions] = useState('');

  // Auto-derive `selectedShape` from the cutline picker so pricing,
  // submission, and the PDF export all read from a single source of
  // truth. 'die-cut' = custom outline; 'standard' = pre-defined rect.
  const [selectedShape, setSelectedShape] = useState('die-cut');
  useEffect(() => {
    if (!page2CutlineShape) return;
    setSelectedShape(page2CutlineShape === 'custom' ? 'die-cut' : 'standard');
  }, [page2CutlineShape]);

  return {
    page2ArtworkFile,
    setPage2ArtworkFile,
    page2ArtworkId,
    setPage2ArtworkId,
    page2CutlineShape,
    setPage2CutlineShape,
    page2ArtworkInstructions,
    setPage2ArtworkInstructions,
    selectedShape,
    setSelectedShape,
  };
}
